import React, { useEffect, useRef, useState, useCallback } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
} from "@stripe/react-connect-js";
import { startConnectOnboarding, getConnectStatus } from "../../api/paymentsApi";
import "./FinancePage.css";

/**
 * Embedded Stripe Connect onboarding — rendered entirely IN-ABON (the tenant
 * never visits or logs into Stripe). Accounts v2, KYC owned by Stripe. Chrome
 * uses the shared FinancePage.css so it sits natively next to the rest of the
 * CRM; the embedded component is themed from the live CRM accent token.
 * Ongoing management (payouts/balances) will be added as sibling embedded
 * components on the same connectInstance.
 */
function brandAppearance() {
  // Match the CRM theme (dark/light aware): read the resolved accent + fonts
  // from the document so the embedded component doesn't look foreign.
  // Stripe needs a resolved value, not a var() reference, so the token is read
  // off the document rather than written literally — no hardcoded fallback
  // hex, which is also what the color ratchet requires. Without a document
  // (SSR) we simply omit colorPrimary and Stripe uses its own default; this
  // component only ever renders client-side.
  let accent = "";
  let fontFamily = "Inter, system-ui, sans-serif";
  try {
    const cs = getComputedStyle(document.documentElement);
    accent = (cs.getPropertyValue("--accent") || "").trim();
    fontFamily = (cs.getPropertyValue("--font-sans") || "").trim() || fontFamily;
  } catch (_) {
    /* SSR / no document — Stripe defaults apply */
  }
  return {
    variables: {
      ...(accent ? { colorPrimary: accent } : {}),
      fontFamily,
      borderRadius: "12px",
    },
  };
}

export default function ConnectOnboarding() {
  const [connectInstance, setConnectInstance] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const startedRef = useRef(false);

  // Only reached after the owner clicked Connect Stripe (connect-js calls it
  // again when the session expires), so it carries the explicit confirm.
  const fetchClientSecret = useCallback(async () => {
    const { data } = await startConnectOnboarding({ confirm: true });
    return data.client_secret;
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await getConnectStatus({ refresh: 1 });
      setStatus(data);
    } catch (_) {
      /* status is best-effort */
    }
  }, []);

  // Opening this page is a READ: status only. It never creates a Stripe
  // account (QA 2026-10-18: the old mount-time POST minted live accounts).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getConnectStatus();
        if (cancelled) return;
        if (data?.reason === "fieldroutes_tenant") {
          setError("Billing for this account is handled in FieldRoutes.");
        } else {
          setStatus(data);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || "Unable to load payment status.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Explicit owner action: creates the connected account (once — the backend
  // is idempotent per tenant) and opens the embedded onboarding.
  const connectStripe = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setStarting(true);
    setError(null);
    try {
      const { data } = await startConnectOnboarding({ confirm: true });
      const instance = await loadConnectAndInitialize({
        publishableKey: data.publishable_key,
        fetchClientSecret,
        appearance: brandAppearance(),
      });
      setConnectInstance(instance);
      setStatus((s) => ({ ...(s || {}), connected: true, onboarding_status: data.onboarding_status }));
    } catch (e) {
      const code = e?.response?.data?.code;
      setError(code === "fieldroutes_tenant"
        ? "Billing for this account is handled in FieldRoutes."
        : e?.response?.data?.error || "Unable to start payment onboarding.");
      startedRef.current = false;
    } finally {
      setStarting(false);
    }
  }, [fetchClientSecret]);

  const connected = !!status?.connected;

  return (
    <div className="finance-page">
      <div className="finance-shell">
        <div className="finance-head">
          <div>
            <h1>Set up payments</h1>
            <p className="finance-muted">
              Complete a few details to start accepting card &amp; bank payments.
              Everything happens right here — you never leave Abon.
            </p>
          </div>
          {status?.charges_enabled && (
            <span className="finance-pill success">Payments active</span>
          )}
        </div>

        <div className="finance-card">
          {error && (
            <p className="finance-muted danger" role="alert">
              {error}
            </p>
          )}
          {loading && <p className="finance-muted">Loading payment status…</p>}
          {!loading && !connectInstance && status && (
            <>
              <p className="finance-muted">
                {connected
                  ? `Stripe account connected — setup ${status.onboarding_status === "complete" ? "complete" : "not finished"}.`
                  : "No Stripe account connected"}
              </p>
              {!status.charges_enabled && (
                <button
                  type="button"
                  className="finance-btn"
                  onClick={connectStripe}
                  disabled={starting}
                >
                  {starting ? "Connecting…" : connected ? "Continue setup" : "Connect Stripe"}
                </button>
              )}
            </>
          )}
          {connectInstance && (
            <ConnectComponentsProvider connectInstance={connectInstance}>
              <ConnectAccountOnboarding onExit={refreshStatus} />
            </ConnectComponentsProvider>
          )}
        </div>
      </div>
    </div>
  );
}
